/**
 * 表格提取服务
 * 从 Markdown 内容中提取表格数据并转换为 CSV 和 Excel 格式
 */

/**
 * 表格数据接口
 */
export interface TableData {
  title: string
  headers: string[]
  rows: string[][]
  startIndex: number
}

/**
 * 清理 Markdown 格式标记，返回纯文本
 * @param text 包含 Markdown 标记的文本
 * @returns 清理后的纯文本
 */
export const cleanMarkdownFormat = (text: string): string => {
  if (!text) return ''
  
  return text
    // 移除粗体标记 **text**
    .replace(/\*\*(.*?)\*\*/g, '$1')
    // 移除斜体标记 *text*
    .replace(/\*(.*?)\*/g, '$1')
    // 移除行内代码标记 `text`
    .replace(/`(.*?)`/g, '$1')
    // 移除链接 [text](url)，只保留文本
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // 移除图片 ![alt](url)，只保留alt文本
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
    // 移除删除线标记 ~~text~~
    .replace(/~~(.*?)~~/g, '$1')
    // 移除下划线标记 __text__
    .replace(/__(.*?)__/g, '$1')
    // 移除行内 HTML 标签
    .replace(/<[^>]*>/g, '')
    // 清理多余的空格
    .trim()
}

/**
 * 解析 Markdown 表格
 * @param markdown Markdown 内容
 * @returns 提取的表格数据数组
 */
export const extractTablesFromMarkdown = (markdown: string): TableData[] => {
  const tables: TableData[] = []
  const lines = markdown.split('\n')
  let currentTable: TableData | null = null
  let tableStartIndex = 0
  let isHeaderSet = false

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim()
    
    // 检测表格开始（包含 | 的行）
    if (line.includes('|') && !line.startsWith('```')) {
      // 如果没有当前表格，创建新表格
      if (!currentTable) {
        currentTable = {
          title: `表格 ${tables.length + 1}`,
          headers: [],
          rows: [],
          startIndex: tableStartIndex
        }
        isHeaderSet = false
      }
      
      // 解析表格行并清理Markdown格式
      const cells = line.split('|')
        .map(cell => cleanMarkdownFormat(cell.trim()))
        .filter(cell => cell !== '')
      
      // 跳过空行
      if (cells.length === 0) {
        continue
      }
      
      // 检查是否是分隔行（如 |---|---|）
      if (cells.every(cell => /^[-\s:]+$/.test(cell))) {
        // 这是分隔行，跳过它
        isHeaderSet = true
        continue
      }
      
      // 如果还没有设置表头，设置表头
      if (!isHeaderSet && currentTable.headers.length === 0) {
        currentTable.headers = cells
        isHeaderSet = true
      } else if (isHeaderSet && cells.length > 0) {
        // 数据行
        currentTable.rows.push(cells)
      }
    } else {
      // 如果遇到非表格行且当前有表格，结束当前表格
      if (currentTable && currentTable.headers.length > 0) {
        tables.push(currentTable)
        currentTable = null
        isHeaderSet = false
        tableStartIndex = i + 1
      }
    }
  }
  
  // 添加最后一个表格
  if (currentTable && currentTable.headers.length > 0) {
    tables.push(currentTable)
  }
  
  return tables
}

/**
 * 将表格数据转换为 CSV 格式
 * @param tables 表格数据数组
 * @param options 导出选项
 * @returns CSV 字符串
 */
export const convertTablesToCSV = (tables: TableData[], options: any = {}): string => {
  const { includeTitles = true, separator = ',' } = options
  let csvContent = ''
  
  tables.forEach((table, tableIndex) => {
    // 添加表格标题（如果启用）
    if (includeTitles && table.title) {
      csvContent += `# ${table.title}\n\n`
    }
    
    // 添加表头
    if (table.headers.length > 0) {
      csvContent += table.headers.map(header => escapeCSVField(header)).join(separator) + '\n'
    }
    
    // 添加数据行
    table.rows.forEach(row => {
      // 确保行数据与表头列数一致
      const alignedRow = [...row]
      while (alignedRow.length < table.headers.length) {
        alignedRow.push('')
      }
      
      csvContent += alignedRow.map(cell => escapeCSVField(cell)).join(separator) + '\n'
    })
    
    // 表格之间添加空行（除了最后一个表格）
    if (tableIndex < tables.length - 1) {
      csvContent += '\n'
    }
  })
  
  return csvContent
}

/**
 * 将表格数据转换为 Excel 工作表格式
 * @param tables 表格数据数组
 * @param options 导出选项
 * @returns Excel 工作表数据
 */
export const convertTablesToExcel = (tables: TableData[], options: any = {}): any[] => {
  const { includeTitles = true } = options
  const worksheets: any[] = []
  
  tables.forEach((table) => {
    const worksheet: any[] = []
    
    // 添加表格标题（如果启用）
    if (includeTitles && table.title) {
      worksheet.push([table.title])
      worksheet.push([]) // 空行
    }
    
    // 添加表头
    if (table.headers.length > 0) {
      worksheet.push([...table.headers])
    }
    
    // 添加数据行
    table.rows.forEach(row => {
      // 确保行数据与表头列数一致
      const alignedRow = [...row]
      while (alignedRow.length < table.headers.length) {
        alignedRow.push('')
      }
      worksheet.push(alignedRow)
    })
    
    worksheets.push(worksheet)
  })
  
  return worksheets
}

/**
 * 转义 CSV 字段（处理逗号、引号、换行符）
 * @param field 字段内容
 * @returns 转义后的字段
 */
function escapeCSVField(field: string): string {
  // 如果包含逗号、引号或换行符，需要用引号包围并转义内部引号
  if (field.includes(',') || field.includes('"') || field.includes('\n')) {
    return '"' + field.replace(/"/g, '""') + '"'
  }
  return field
}

/**
 * 表格数据接口
 */
/**
 * 获取表格统计信息
 * @param tables 表格数据数组
 * @returns 统计信息
 */
export const getTableStatistics = (tables: TableData[]): {
  totalTables: number
  totalRows: number
  totalColumns: number
  tableDetails: Array<{
    title: string
    rows: number
    columns: number
  }>
} => {
  let totalRows = 0
  let totalColumns = 0
  
  const tableDetails = tables.map(table => {
    const rowCount = table.rows.length
    const columnCount = table.headers.length
    totalRows += rowCount
    totalColumns = Math.max(totalColumns, columnCount)
    
    return {
      title: table.title,
      rows: rowCount,
      columns: columnCount
    }
  })
  
  return {
    totalTables: tables.length,
    totalRows,
    totalColumns,
    tableDetails
  }
}